/**
 * Facturación electrónica (ORR-255). La emisión es asíncrona: la function deja la factura `pendiente` y
 * la background la emite. Mientras haya facturas en vuelo, las consultas se refrescan cada 3 segundos.
 */
import { useMemo } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso, handleMutationError } from '@/lib/permisos-guard'
import {
  ESTADOS_EN_VUELO,
  facturasService,
  type FacturaResumen,
  type FiltrosFacturas,
  type LineaManual,
  type PaginaFacturas,
  type ReceptorManual,
} from '@/services/facturasService'
import type { Permisos } from '@/types/roles'

export const facturasKeys = {
  all: ['facturas'] as const,
  emisor: (orgId: string | undefined) => [...facturasKeys.all, 'emisor', orgId] as const,
  porMovimientos: (orgId: string | undefined, ids: string[]) => [...facturasKeys.all, 'movimientos', orgId, ids] as const,
  listado: (orgId: string | undefined, filtros: FiltrosFacturas) => [...facturasKeys.all, 'listado', orgId, filtros] as const,
  acreditado: (orgId: string | undefined, facturaId: string | undefined) => [...facturasKeys.all, 'acreditado', orgId, facturaId] as const,
}

const REFRESCO_EN_VUELO_MS = 3000

/** Busca una factura de la organización por id. Es undefined mientras no hay cliente ni organización. */
export function useBuscarFactura() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  return useMemo(
    () => (supabase && organizacionId ? (id: string) => facturasService.obtener(supabase, organizacionId, id) : undefined),
    [supabase, organizacionId],
  )
}

/** Emisor de la organización. `activo` decide si se ofrece facturar. También lo lee Configuración. */
export function useEmisorFacturacion() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  return useQuery({
    queryKey: facturasKeys.emisor(organizacionId ?? undefined),
    queryFn: () => facturasService.obtenerEmisor(supabase!, organizacionId!),
    enabled: !!supabase && !!organizacionId && (puede.verFacturas === true || puede.verConfiguracion === true),
    staleTime: 5 * 60_000,
  })
}

type DatosEmisor = Parameters<typeof facturasService.guardarEmisor>[2]

/** Alta o cambio del emisor desde Configuración (editarConfiguracion, igual que la RPC). */
export function useGuardarEmisorFacturacion() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (datos: DatosEmisor) => {
      assertPermiso(puede, 'editarConfiguracion')
      if (!supabase || !organizacionId) throw new Error('No hay organización activa.')
      return facturasService.guardarEmisor(supabase, organizacionId, datos)
    },
    onSuccess: () => {
      toast.success('Emisor de facturación guardado')
      return queryClient.invalidateQueries({ queryKey: facturasKeys.emisor(organizacionId ?? undefined) })
    },
    onError: (error) => handleMutationError(error, 'No se pudo guardar el emisor de facturación'),
  })
}

/** Puede emitir: tiene el permiso y el estudio tiene un emisor activo. */
export function usePuedeFacturar(): boolean {
  const { puede } = usePermissions()
  const { data: emisor } = useEmisorFacturacion()
  return puede.emitirFactura === true && emisor?.activo === true
}

/** Factura más reciente de cada cobro de la lista. */
export function useFacturasDeMovimientos(movimientoIds: string[]) {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const ids = [...new Set(movimientoIds)].sort()
  return useQuery({
    queryKey: facturasKeys.porMovimientos(organizacionId ?? undefined, ids),
    queryFn: () => facturasService.porMovimientos(supabase!, organizacionId!, ids),
    enabled: !!supabase && !!organizacionId && puede.verFacturas === true && ids.length > 0,
    refetchInterval: (query) => {
      const data = query.state.data as Record<string, FacturaResumen> | undefined
      return data && Object.values(data).some((f) => ESTADOS_EN_VUELO.includes(f.estado)) ? REFRESCO_EN_VUELO_MS : false
    },
  })
}

/** Listado de la página Facturas. Se refresca solo mientras alguna de la página está en vuelo. */
export function useFacturas(filtros: FiltrosFacturas) {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  return useQuery({
    queryKey: facturasKeys.listado(organizacionId ?? undefined, filtros),
    queryFn: () => facturasService.listar(supabase!, organizacionId!, filtros),
    enabled: !!supabase && !!organizacionId && puede.verFacturas === true,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const data = query.state.data as PaginaFacturas | undefined
      return data?.filas.some((f) => ESTADOS_EN_VUELO.includes(f.estado)) ? REFRESCO_EN_VUELO_MS : false
    },
  })
}

/** Lo que ya acreditan las notas de crédito de una factura, para el diálogo de la nota de crédito parcial. */
export function useAcreditadoDeFactura(facturaId: string | undefined) {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  return useQuery({
    queryKey: facturasKeys.acreditado(organizacionId ?? undefined, facturaId),
    queryFn: () => facturasService.acreditado(supabase!, organizacionId!, facturaId!),
    enabled: !!supabase && !!organizacionId && !!facturaId && puede.verFacturas === true,
    // Se lee al abrir el diálogo: una nota de crédito de hace un minuto tiene que contar.
    staleTime: 0,
  })
}

type Accion =
  | { tipo: 'emitirDesdeCobro'; movimientoId: string; enviarEmail: boolean }
  | { tipo: 'emitirManual'; receptor: ReceptorManual; lineas: LineaManual[]; enviarEmail: boolean; solicitudId: string }
  | { tipo: 'anular'; facturaId: string; enviarEmail: boolean }
  | { tipo: 'acreditar'; facturaId: string; importe: number; concepto: string; enviarEmail: boolean; solicitudId: string }
  | { tipo: 'reintentar'; facturaId: string }
  | { tipo: 'reconsultar'; facturaId: string }
  | { tipo: 'enviarEmail'; facturaId: string; email?: string }

/** Flag que pide cada acción; el servidor exige el mismo. */
export const permisoDeAccion = (tipo: Accion['tipo']): keyof Permisos =>
  tipo === 'reconsultar' ? 'verFacturas' : tipo === 'anular' || tipo === 'acreditar' ? 'anularFactura' : 'emitirFactura'

export function useAccionFactura() {
  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (accion: Accion) => {
      assertPermiso(puede, permisoDeAccion(accion.tipo))
      if (!organizacionId) throw new Error('No hay organización activa.')
      const token = await getAccessTokenSilently()
      switch (accion.tipo) {
        case 'emitirDesdeCobro':
          return facturasService.emitirDesdeCobro(token, organizacionId, accion.movimientoId, accion.enviarEmail)
        case 'emitirManual':
          return facturasService.emitirManual(token, organizacionId, accion.receptor, accion.lineas, accion.enviarEmail, accion.solicitudId)
        case 'anular':
          return facturasService.anular(token, organizacionId, accion.facturaId, accion.enviarEmail)
        case 'acreditar':
          return facturasService.acreditar(token, organizacionId, accion.facturaId, accion.importe, accion.concepto, accion.enviarEmail, accion.solicitudId)
        case 'reintentar':
          return facturasService.reintentar(token, organizacionId, accion.facturaId)
        case 'reconsultar':
          return facturasService.reconsultar(token, organizacionId, accion.facturaId)
        case 'enviarEmail':
          return facturasService.enviarEmail(token, organizacionId, accion.facturaId, accion.email)
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: facturasKeys.all }),
    onError: (error) => handleMutationError(error, 'No se pudo completar la operación de facturación'),
  })
}

export function useDescargarPdfFactura() {
  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  return useMutation({
    mutationFn: async (facturaId: string) => {
      assertPermiso(puede, 'verFacturas')
      if (!organizacionId) throw new Error('No hay organización activa.')
      const { blob, nombre } = await facturasService.descargarPdf(await getAccessTokenSilently(), organizacionId, facturaId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nombre
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    },
    onError: (error) => handleMutationError(error, 'No se pudo descargar la factura'),
  })
}
